import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import CampaignList from './CampaignList';
import CampaignMetrics from './CampaignMetrics';
import CampaignForm from './CampaignForm';
import { useZohoCampaigns } from '../../hooks/useZohoCampaigns';
import { Campaign } from '../../types/campaign';

export default function CampaignDashboard() {
  const { 
    campaigns, 
    loading, 
    error,
    createCampaign,
    updateCampaign,
    deleteCampaign 
  } = useZohoCampaigns();

  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const handleAddCampaign = async (campaignData: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => {
    await createCampaign(campaignData);
    setShowForm(false);
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const handleUpdateCampaign = async (campaignData: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingCampaign) {
      await updateCampaign(editingCampaign.id, campaignData);
      setShowForm(false);
      setEditingCampaign(undefined);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (confirm('Are you sure you want to delete this campaign?')) {
      await deleteCampaign(campaignId);
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType ? campaign.type === filterType : true;
    const matchesStatus = filterStatus ? campaign.status === filterStatus : true;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (error) {
    return <div className="text-red-600 p-4">Error loading campaigns: {error}</div>;
  }

  return (
    <div className="h-full space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Campaign Management</h2>
          <p className="mt-2 text-gray-600">Track and manage M&A outreach campaigns</p>
        </div>
        
        <button
          onClick={() => {
            setEditingCampaign(undefined);
            setShowForm(true);
          }}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 inline-block mr-2" />
          New Campaign
        </button>
      </div>

      <CampaignMetrics campaigns={campaigns} />

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="acquisition">Acquisition</option>
              <option value="merger">Merger</option>
              <option value="divestiture">Divestiture</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <CampaignList
          campaigns={filteredCampaigns}
          loading={loading}
          onEdit={handleEditCampaign}
          onDelete={handleDeleteCampaign}
        />
      </div>

      {showForm && (
        <CampaignForm
          campaign={editingCampaign}
          onSubmit={editingCampaign ? handleUpdateCampaign : handleAddCampaign}
          onClose={() => {
            setShowForm(false);
            setEditingCampaign(undefined);
          }}
        />
      )}
    </div>
  );
}